import { FormEvent, useCallback, useEffect, useState } from "react";
import { X } from "lucide-react";
import { createCheck, fetchCheckById, updateCheck, type Check } from "../../api/check";
import { useNotification } from "../Notification/NotificationProvider";
import { ModalPortal } from "../ModalPortal/ModalPortal";
import { CircularProgress } from "../CircularProgress/CircularProgress";
import { FAILED_CHECK_COLOR_OPTIONS } from "../../utils/checkFormat";
import { FormDropdown } from "../FormDropdown/FormDropdown";
import {
  CheckFormValues,
  checkToForm,
  emptyCheckForm,
  formToCheck,
  validateCheckForm,
} from "./checkForm";
import "./CheckModal.scss";

interface CheckModalProps {
  checkId?: string | null;
  onClose: () => void;
  onSaved: (check: Check) => void;
}

export const CheckModal = ({ checkId, onClose, onSaved }: CheckModalProps) => {
  const { showNotification } = useNotification();
  const isCreate = !checkId;
  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(isCreate);
  const [form, setForm] = useState<CheckFormValues>(emptyCheckForm);

  useEffect(() => {
    if (!checkId) {
      setForm(emptyCheckForm());
      setIsEditing(true);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setIsEditing(false);

    fetchCheckById(checkId)
      .then((item) => {
        if (!cancelled) {
          setForm(checkToForm(item));
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [checkId, showNotification]);

  const readOnly = !isEditing;

  const persistCheck = useCallback(
    async (nextForm: CheckFormValues) => {
      let saved: Check;

      if (isCreate) {
        saved = await createCheck(formToCheck(nextForm));
        showNotification({ message: "Проверка создана", variant: "success" });
      } else if (checkId) {
        saved = await updateCheck(checkId, formToCheck(nextForm, checkId));
        showNotification({ message: "Проверка обновлена", variant: "success" });
      } else {
        return;
      }

      onSaved(saved);
      onClose();
    },
    [checkId, isCreate, onClose, onSaved, showNotification]
  );

  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      const validationError = validateCheckForm(form);
      if (validationError) {
        showNotification({ message: validationError, variant: "error" });
        return;
      }

      setSaving(true);

      try {
        await persistCheck(form);
      } catch (err: unknown) {
      } finally {
        setSaving(false);
      }
    },
    [form, persistCheck, showNotification]
  );

  const title = isCreate ? "Создать проверку" : isEditing ? "Редактировать проверку" : "Проверка";

  return (
    <ModalPortal>
      <div className="detail-checks-modal-overlay" onClick={onClose}>
        <div
          className="detail-checks-modal detail-checks-modal--check"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="detail-checks-modal-head">
            <h3>{title}</h3>
            <button type="button" className="detail-tasklist-close" onClick={onClose} aria-label="Закрыть">
              <X size={18} />
            </button>
          </div>

          {loading ? (
            <div className="category-modal-loading">
              <CircularProgress size="lg" />
            </div>
          ) : (
            <>
              <form
                id="check-modal-form"
                className="detail-checks-modal-body create-request-modal-form check-modal-form"
                onSubmit={handleSubmit}
              >
              <div className="create-request-modal-form-group">
                <label className="create-request-modal-label">Код</label>
                <input
                  type="text"
                  className="create-request-modal-input"
                  value={form.code}
                  disabled={readOnly}
                  autoFocus={isEditing}
                  onChange={(e) => setForm((current) => ({ ...current, code: e.target.value }))}
                />
              </div>

              <div className="create-request-modal-form-group">
                <label className="create-request-modal-label">Описание</label>
                <textarea
                  className="create-request-modal-input check-modal-textarea"
                  value={form.description}
                  disabled={readOnly}
                  rows={3}
                  onChange={(e) =>
                    setForm((current) => ({ ...current, description: e.target.value }))
                  }
                />
              </div>

              <div className="create-request-modal-form-group">
                <label className="create-request-modal-label">Ответственность</label>
                <textarea
                  className="create-request-modal-input check-modal-textarea"
                  value={form.responsibility}
                  disabled={readOnly}
                  rows={3}
                  onChange={(e) =>
                    setForm((current) => ({ ...current, responsibility: e.target.value }))
                  }
                />
              </div>

              <div className="create-request-modal-form-group">
                <label className="create-request-modal-label">Текст для успешного отображения</label>
                <input
                  type="text"
                  className="create-request-modal-input"
                  value={form.successText}
                  disabled={readOnly}
                  onChange={(e) =>
                    setForm((current) => ({ ...current, successText: e.target.value }))
                  }
                />
              </div>

              <div className="create-request-modal-form-group">
                <label className="create-request-modal-label">Текст ошибки</label>
                <input
                  type="text"
                  className="create-request-modal-input"
                  value={form.failText}
                  disabled={readOnly}
                  onChange={(e) =>
                    setForm((current) => ({ ...current, failText: e.target.value }))
                  }
                />
              </div>

              <div className="create-request-modal-form-group">
                <label className="create-request-modal-label">Рекомендация</label>
                <textarea
                  className="create-request-modal-input check-modal-textarea"
                  value={form.suggestion}
                  disabled={readOnly}
                  rows={3}
                  onChange={(e) =>
                    setForm((current) => ({ ...current, suggestion: e.target.value }))
                  }
                />
              </div>

              <div className="create-request-modal-form-group">
                <label className="create-request-modal-label">
                  Цвет проверки в отчете, если не прошла
                </label>
                <FormDropdown
                  value={form.failedCheckColor}
                  options={FAILED_CHECK_COLOR_OPTIONS}
                  disabled={readOnly}
                  onChange={(failedCheckColor) =>
                    setForm((current) => ({ ...current, failedCheckColor }))
                  }
                />
              </div>

              <label
                className={`check-modal-active${readOnly ? " check-modal-active--disabled" : ""}`}
              >
                <input
                  type="checkbox"
                  checked={form.active}
                  disabled={readOnly}
                  onChange={(e) =>
                    setForm((current) => ({ ...current, active: e.target.checked }))
                  }
                />
                <span>Активна</span>
              </label>

              </form>

              <div className="detail-checks-modal-footer category-modal-actions">
                {!isCreate && !isEditing && (
                  <button
                    type="button"
                    className="category-modal-secondary-btn"
                    onClick={() => setIsEditing(true)}
                  >
                    Редактировать
                  </button>
                )}
                {!isCreate && isEditing && (
                  <button
                    type="button"
                    className="category-modal-secondary-btn"
                    disabled={saving}
                    onClick={() => {
                      setIsEditing(false);
                      if (checkId) {
                        setLoading(true);
                        fetchCheckById(checkId)
                          .then((item) => setForm(checkToForm(item)))
                          .finally(() => setLoading(false));
                      }
                    }}
                  >
                    Отмена
                  </button>
                )}
                {(isCreate || isEditing) && (
                  <button
                    type="submit"
                    form="check-modal-form"
                    className="create-request-modal-submit"
                    disabled={saving}
                  >
                    {saving ? <CircularProgress size="sm" /> : isCreate ? "Создать" : "Сохранить"}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </ModalPortal>
  );
};
